import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.jsx'
import pwaErrorHandler from './utils/pwaErrorHandler.js'
import AdminMilestoneService from './services/AdminMilestoneService.js'

// Expose AdminMilestoneService globally for admin console access
window.AdminMilestoneService = AdminMilestoneService;

// Initialize PWA error handler (auto-starts global error handling)
console.log('PWA Error Handler initialized');

// Override console methods to filter out browser extension noise
const originalConsoleLog = console.log;
const originalConsoleWarn = console.warn;
const originalConsoleError = console.error;

// Filter out known browser extension messages
const isExtensionNoise = (message) => {
  if (typeof message !== 'string') return false;
  return (
    message.includes('Content Script Bridge: Unknown message type') ||
    message.includes('DFP: Breach notification') ||
    message.includes('Loaded contentScript') ||
    message.includes('content-scripts.js')
  );
};

// Override console methods to filter extension noise
console.log = (...args) => {
  if (!args.some(arg => isExtensionNoise(String(arg)))) {
    originalConsoleLog.apply(console, args);
  }
};

console.warn = (...args) => {
  if (!args.some(arg => isExtensionNoise(String(arg)))) {
    originalConsoleWarn.apply(console, args);
  }
};

// Safe console.error override that doesn't interfere with React error handling
console.error = (...args) => {
  const message = args.join(' ');
  // Skip React-specific errors and setState warnings to prevent infinite loops
  if (message.includes('setState') || message.includes('Maximum update depth') || message.includes('Warning:')) {
    originalConsoleError.apply(console, args);
    return;
  }
  
  if (!args.some(arg => isExtensionNoise(String(arg)))) {
    originalConsoleError.apply(console, args);
  }
};

// Prevent content script bridge errors from browser extensions
window.addEventListener('message', (event) => {
  // Silently handle browser extension messages
  return;
});

// Also handle any global errors from extensions
window.addEventListener('error', (event) => {
  if (event.filename && event.filename.includes('content-scripts.js')) {
    event.preventDefault(); // Prevent the error from showing
    return false;
  }
});

// Register Service Worker for PWA functionality - Enhanced Error Handling 2025-07-09-v13
if ('serviceWorker' in navigator) {
  let updateNotificationShown = false;
  let registrationRetries = 0;
  const MAX_RETRIES = 3;
  
  // Enhanced error handling wrapper
  const safeServiceWorkerOperation = async (operation, context) => {
    try {
      return await operation();
    } catch (error) {
      console.warn(`Service Worker ${context} error:`, error);
      
      // Don't let SW errors crash the app
      if (error.name === 'SecurityError' || error.name === 'NotSupportedError') {
        console.warn('Service Worker not supported or blocked, app will continue normally');
        return null;
      }
      
      // For other errors, log but don't throw
      console.error('Service Worker operation failed:', context, error);
      return null;
    }
  };
  
  // Safe message handler to prevent crashes from malformed messages
  const handleServiceWorkerMessage = (event) => {
    try {
      if (!event.data || typeof event.data !== 'object') {
        console.warn('Invalid SW message format:', event.data);
        return;
      }
      
      console.log('SW message received:', event.data);
      
      if (event.data.type === 'SW_UPDATE_AVAILABLE' && !updateNotificationShown) {
        console.log('Service worker update available:', event.data.version);
        updateNotificationShown = true;
        showUpdateNotification();
      } else if (event.data.type === 'SW_ACTIVATED') {
        console.log('Service worker activated:', event.data.version);
      }
    } catch (error) {
      console.warn('Error handling SW message:', error);
    }
  };
  
  // Safe update check with retry logic
  const safeUpdateCheck = async (registration) => {
    return safeServiceWorkerOperation(async () => {
      await registration.update();
      console.log('Service worker update check completed');
    }, 'update check');
  };
  
  // Enhanced registration with retry logic
  const registerServiceWorker = async (retryCount = 0) => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none' // Prevent caching issues
      });
      
      console.log('SW registered v13: ', registration);
      
      // Safely add message listener
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
      
      // Safe update check
      await safeUpdateCheck(registration);
      
      // Enhanced updatefound handler with error boundaries
      registration.addEventListener('updatefound', () => {
        try {
          const newWorker = registration.installing;
          if (!newWorker) {
            console.warn('No installing worker found during updatefound');
            return;
          }
          
          console.log('SW update found, installing...');
          
          newWorker.addEventListener('statechange', () => {
            try {
              if (newWorker.state === 'installed') {
                if (navigator.serviceWorker.controller && !updateNotificationShown) {
                  console.log('New SW installed, ready to activate');
                  updateNotificationShown = true;
                  showUpdateNotification();
                } else {
                  console.log('SW installed for first time');
                }
              } else if (newWorker.state === 'error') {
                console.warn('Service worker installation failed');
              }
            } catch (error) {
              console.warn('Error in SW statechange handler:', error);
            }
          });
        } catch (error) {
          console.warn('Error in SW updatefound handler:', error);
        }
      });
      
      return registration;
    } catch (error) {
      console.error('SW registration failed:', error);
      
      // Retry logic for transient failures
      if (retryCount < MAX_RETRIES && 
          !error.name.includes('SecurityError') && 
          !error.name.includes('NotSupportedError')) {
        console.log(`Retrying SW registration (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return registerServiceWorker(retryCount + 1);
      }
      
      // After max retries or for security errors, log but don't crash
      console.warn('Service Worker registration permanently failed, app will continue without PWA features');
      return null;
    }
  };
  
  // Global error handler for unhandled service worker errors
  window.addEventListener('error', (event) => {
    if (event.filename && event.filename.includes('sw.js')) {
      console.warn('Service Worker error caught:', event.error);
      event.preventDefault();
      return false;
    }
  });
  
  // Global handler for unhandled promise rejections from SW
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && event.reason.toString().includes('service worker')) {
      console.warn('Service Worker promise rejection caught:', event.reason);
      event.preventDefault();
      return false;
    }
  });
  
  // Initialize service worker when page loads
  window.addEventListener('load', () => {
    registerServiceWorker();
  });
}

// Show update notification function (legacy fallback)
function showUpdateNotification() {
  // Let the React component handle this now
  console.log('🔄 Service Worker update detected - React component will show notification');
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
